/**
 * simulation.js — The rules of the fight, with nothing attached.
 *
 * Everything that decides what HAPPENS in a match lives here: input handling,
 * physics, the board, beams, melee, ordnance, deaths and respawns, the push
 * from screen to screen, and the win. None of it touches React, the DOM,
 * `window`, storage, audio or the network, so the same code that runs the
 * match in the host's browser tab can run headless in a server that has to
 * referee or replay one. That is what staked PvP needs: today the host's own
 * browser declares the winner, which is a payout button, not a trust model.
 *
 * The whole match is one plain object (createGameState), and every function
 * takes the parts of it it touches as arguments — nothing is held between
 * calls. The caller owns the clock: stepGame advances exactly STEP_MS of
 * simulated time per call.
 *
 * Two things come in from outside, both handed to stepGame:
 *
 *   readInput(playerId, player, game) -> input frame, or null for none
 *     Asked once per fighter per step, at the moment that fighter gets to act:
 *     a stunned, floored or dying fighter is not asked at all, and player two
 *     is asked after player one's input has already been applied. Keyboard,
 *     controller, network, AI or a recorded replay is the caller's business.
 *
 *   fx — the side effects the rules cause but do not own:
 *     sound(name)             Every sound cue, by sound.js name. The browser
 *                             plays it, shakes the camera to it, and queues it
 *                             for the online guest.
 *     particle(x, y, type)    A cosmetic burst of sparks.
 *     screenChange(fromScreen, direction, advancer)
 *                             The fight broke into the next room: the camera
 *                             dolly, and the gate's sparks replacing the old
 *                             room's.
 *     recordWin(mode, slot, difficulty)
 *                             The all-time tally (score.js). What it returns
 *                             is kept as game.matchRecord.
 *     settleSeries(stake, difficulty, won)
 *                             Pay out a decided staked series (wallet.js). What
 *                             it returns is kept as game.payout.
 *   NO_FX implements all of them as nothing, for a caller that wants none.
 *
 * Callbacks rather than a list of events handed back after the step, because
 * two of these have to happen at the point the rules reach them: a screen
 * change wipes sparks raised earlier in the same step, and the settlement
 * results are written into the state that step goes on to broadcast.
 * Deferring either would reorder what the player sees.
 *
 * Determinism: the one roll that changes the fight — where a cluster shell's
 * submunitions scatter — comes from a seeded generator whose state is part of
 * the game (game.rng). From the same starting state and the same input frames,
 * a step produces the same result wherever it runs. Everything decorative that
 * wants randomness (sparks, fireworks, camera shake) lives on the far side of
 * fx and never draws from it.
 */

import {
  screenWidth,
  START_SCREEN,
  SCREEN_COUNT,
  CANVAS_HEIGHT,
  GRAVITY,
  FRICTION,
  BOARD_SPEED,
  BOARD_ACCEL,
  BOARD_CLIMB_SPEED,
  PLAYER_SPEED,
  JUMP_FORCE,
  BEAM_RECHARGE_TIME,
  STEP_MS,
  GROUND_Y,
  CEILING_Y,
  spawnAt,
  BODY_HALF_WIDTH,
  BODY_TOP,
  BODY_BOTTOM,
  BOARD_HALF_WIDTH,
  BOARD_HALF_HEIGHT,
  MUZZLE_DX,
  MUZZLE_DY,
  BEAM_CHARGE_TIME,
  BEAM_LIFE,
  SPAWN_INVULN_TIME,
  DRAW_TIME,
  STOW_TIME,
  MOUNT_TIME,
  BACKFLIP_TIME,
  VAPORIZE_TIME,
  SHOCK_STUN_TIME,
  BOARD_MOUNT_RANGE,
  fitsThroughCellDoor,
  HIT_STOP_TIME,
  HOLSTER_TAP_WINDOW,
  CLUSTER_MAX,
  CLUSTER_RECHARGE,
  EMP_MAX,
  EMP_RECHARGE,
  THROW_SPEED,
  THROW_LIFT,
  ORDNANCE_GRAVITY,
  CLUSTER_FUSE,
  EMP_SWEEP_TIME,
  CLUSTER_SUBMUNITIONS,
  SUBMUNITION_FUSE,
  SUBMUNITION_SPREAD,
  CLUSTER_BLAST_RADIUS,
  CLUSTER_STUN_TIME,
  EMP_BOARD_DOWN_TIME,
  BLAST_LIFE,
  SCREEN_TRANSITION_HOLD,
} from './constants';
import { getObstacles, raycastX } from './obstacles';
import {
  MOVES,
  MELEE_MAX_HP,
  GUARD_PUSH_FRAC,
  guardStops,
  KNOCKDOWN_TIME,
  HURT_TIME,
  FINISH_RANGE,
  chooseMove,
  isStriking,
  isDiving,
  strikeBox,
} from './melee';

/**
 * Speed at which a board has to be travelling to hurt anyone. A board lying
 * still on the ground is inert, so a dismounted fighter can approach an
 * opponent's board to grab it (and get shocked for trying) instead of simply
 * dying on contact.
 */
const BOARD_LETHAL_SPEED = 5;

/**
 * Side effects that do nothing, for a caller that wants the rules and none of
 * the show — a referee, a replay check, a test. Also the reference for what an
 * fx object has to provide; see the header above.
 */
export const NO_FX = {
  sound: () => {},
  particle: () => {},
  screenChange: () => {},
  recordWin: () => null,
  settleSeries: () => null,
};

/* --- State ------------------------------------------------------------ */

/**
 * Build a player's initial state. Used for the first match and every reset, so
 * spawn position and starting state have exactly one definition.
 *
 * @param {'player1'|'player2'} slot Which side this player fights for.
 * @returns {object} fresh player state including their board.
 */
export const createPlayer = (slot) => ({
  // Matches always open on the landing stage, so that is the screen these are
  // resolved against; every later respawn re-resolves against wherever the
  // fight has been pushed to.
  x: spawnAt(slot, START_SCREEN).x,
  y: spawnAt(slot, START_SCREEN).y,
  vx: 0,
  vy: 0,
  onBoard: true,
  boardMode: 'feet', // 'feet' for riding, 'hands' for shooting
  facingRight: slot === 'player1', // face the middle of the arena
  beamRecharge: 0, // ms until the beam can be fired again
  beamCharge: 0, // ms of windup left before a committed shot goes off
  // Both fighters open the match protected, so the first exchange is about
  // taking position rather than a race to land a free opening shot.
  invuln: SPAWN_INVULN_TIME,
  stunned: 0,
  // Board slung across the back: on foot by CHOICE, with the board still yours
  // rather than lying in the world for anyone to kick back at you.
  holstered: false,
  guardHeld: false,
  holsterTap: 0,
  // The loadout, and the clock on getting it back. Each timer only runs once
  // its magazine is EMPTY — spending one of three shells starts nothing, and
  // spending the last one starts a refill that hands them back one at a time.
  clusterAmmo: CLUSTER_MAX,
  clusterTimer: 0,
  empAmmo: EMP_MAX,
  empTimer: 0,
  // Milliseconds left on a board killed by an EMP. While this is running the
  // board cannot be mounted or recalled and its rider is on foot.
  boardDown: 0,
  vaporizing: 0, // Time left in vaporization animation
  backflipping: 0, // Time left in backflip animation
  rotation: 0, // Current rotation angle for animations
  // Standing on the floor or an obstacle top. Set by updatePhysics where a
  // landing is actually resolved, so nothing has to guess it from velocity.
  grounded: true,
  // Handling move in progress: '' | 'draw' | 'stow' | 'mount'. Kept as two flat
  // scalars rather than an object because the whole player is serialized to the
  // online guest every frame, and scalars survive that round trip unchanged.
  transition: '',
  transitionTime: 0, // ms left in the current handling move
  // Hand-to-hand state. Only ever non-default while on foot with the board
  // gone; completeRespawn puts all of it back.
  hp: MELEE_MAX_HP, // melee-only health — beams and boards still kill outright
  attack: '', // key into melee MOVES, '' when not swinging
  attackTime: 0, // ms left in the current swing
  // Whether the current swing has already connected. One hit per swing, for the
  // same reason a thrown board gets one: without it a limb overlapping its
  // target scores again on every frame it stays there.
  attackHit: false, // host-only, like board.spent — the guest runs no collisions
  downTime: 0, // ms left on the floor after a sweep
  hurtTime: 0, // ms left flinching from a hit
  guarding: false,
  board: {
    x: spawnAt(slot, START_SCREEN).x,
    y: spawnAt(slot, START_SCREEN).y,
    vx: 0,
    vy: 0,
    spin: 0, // cosmetic tumble angle while the board is loose
    // One hit per throw: set when the board connects, cleared when it is
    // kicked or remounted. Host-only simulation state, so it stays off the
    // wire — the online guest never runs collisions.
    spent: false,
    returning: false,
    owner: slot,
  },
});

/**
 * Build a full game state for the start of a match.
 *
 * @param {object} [carry] Values preserved across a restart (mode/difficulty),
 *   plus the running series, if this game is one of a staked best-of-three.
 * @param {number} [carry.seed] Starting state for the game's random stream.
 *   Drawn fresh when not given, so no two casual games scatter alike; a referee
 *   supplies its own and keeps it, since the seed and the input frames are
 *   together the whole replay.
 * @returns {object} fresh game state.
 */
export const createGameState = ({
  gameMode = 'local', difficulty = 'medium', series = null,
  seed = Math.floor(Math.random() * 2 ** 32),
} = {}) => ({
  players: {
    player1: createPlayer('player1'),
    player2: createPlayer('player2'),
  },
  gameMode, // 'local', 'ai', 'online'
  difficulty, // AI difficulty tier id — see DIFFICULTIES in ai.js
  currentScreen: START_SCREEN, // start in the middle; see SCREEN_WIDTHS
  advancementUnlocked: false, // Unlocked after first kill
  advancingPlayer: null, // Who can advance (last killer)
  // Whether the advancer has to LEAVE the doorway before it will let them
  // through. See checkScreenAdvancement: earning the push while already stood
  // in the door used to fire the crossing on that same frame.
  advanceArmed: true,
  winner: null, // who took THIS game — the series may still be running
  // The staked best-of-three, or null for a one-off match that ends at the
  // first portal crossing. Carried across the games in a series by
  // nextGameInSeries, so the tally and the money survive the arena reset.
  //   {wins: {player1, player2}, target, stake, difficulty, game, settled}
  series,
  // Set once a staked series is decided: what the wallet did about it, for the
  // overlay to report. Null while a series is still live.
  payout: null,
  matchRecord: null, // {player1, player2} win tally, set when a match is won
  hitStop: 0, // ms of simulation freeze left after a kill
  // The random stream's state: one unsigned 32-bit word, advanced by
  // nextRandom. Host-only, like board.spent — the guest never rolls anything.
  rng: seed >>> 0,
  // The AI opponent's own stream (aiRandom), from the same seed but kept apart
  // so the AI's rolls never shift the shrapnel scatter: a seed fixes a vs-AI
  // game end to end, and a game without the AI draws exactly as before.
  aiRng: (seed ^ 0x9e3779b9) >>> 0,
  // Cosmetic sparks. The rules never read or write these — fx.particle raises
  // them — but they ride in the state so the online guest gets the host's.
  particles: [],
  beams: [],
  // Devices in flight — thrown canisters and the submunitions a cluster shell
  // splits into. One array for both, because they differ only in what they do
  // when their fuse runs out.
  ordnance: [],
  // Blast rings. Presentation only, but they are host-simulated like everything
  // else so both clients see the same explosion in the same place.
  blasts: [],

  soundEvents: [], // host-side queue of sounds to relay to the online guest
});

/**
 * Draw the next number from the game's own random stream, in [0, 1).
 *
 * mulberry32 over the single word kept in game.rng. Tiny, fast and good enough
 * for scattering shrapnel — and, unlike Math.random, it can be replayed: the
 * same state draws the same numbers wherever the step runs.
 *
 * @param {object} game Game state; its rng word is advanced.
 * @returns {number}
 */
const nextRandom = (game) => drawFrom(game, 'rng');

/**
 * Draw the next number from the AI opponent's stream, in [0, 1). The AI's
 * chance checks roll here instead of Math.random, so a seeded vs-AI game
 * replays exactly (the playtest bridge relies on it).
 *
 * @param {object} game Game state; its aiRng word is advanced.
 * @returns {number}
 */
export const aiRandom = (game) => drawFrom(game, 'aiRng');

/** mulberry32 over the 32-bit word kept in game[word]; advances it. */
const drawFrom = (game, word) => {
  game[word] = (game[word] + 0x6d2b79f5) >>> 0;
  let t = game[word];
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

/* --- The step --------------------------------------------------------- */

/**
 * Advance the match by exactly STEP_MS of simulated time.
 *
 * Returns whether the fight actually moved. It does not once a winner is
 * decided or the opponent has left, nor through a hit stop — and in those
 * frames nothing was asked for input, so the caller must keep any presses it
 * is holding rather than discard them.
 *
 * @param {object} game Game state, mutated in place.
 * @param {function(string, object, object): ?object} readInput
 *   (playerId, player, game) -> input frame or null. See the header.
 * @param {object} fx Side effects; see the header, and NO_FX.
 * @returns {boolean} true if a full step ran and the fighters were asked for
 *   input; false for a frozen step.
 */
export const stepGame = (game, readInput, fx) => {
  // Freeze all simulation once a winner is decided (or the opponent left).
  // The end screen, the rematch and the way back to the menu are the caller's.
  // This also guarantees win-time side effects (victory sound, score tally)
  // fire exactly once rather than every frame.
  if (game.winner || game.disconnected) return false;

  // Hit stop: hold the whole simulation still for a few frames after a kill
  // so the moment of impact reads as an event. Input presses are deliberately
  // held (not cleared) so nothing queued during the freeze is swallowed.
  if (game.hitStop > 0) {
    game.hitStop -= STEP_MS;
    return false;
  }

  // Update players
  Object.keys(game.players).forEach(playerId => {
    const player = game.players[playerId];

    // Reduce stun, beam recharge, spawn protection; handle the backflip.
    if (player.stunned > 0) player.stunned -= STEP_MS;
    if (player.beamRecharge > 0) player.beamRecharge -= STEP_MS;
    if (player.invuln > 0) player.invuln -= STEP_MS;
    // A board killed by an EMP comes back on its own; nothing else revives it
    // except dying, which hands out a fresh one.
    if (player.boardDown > 0) player.boardDown -= STEP_MS;
    if (player.holsterTap > 0) player.holsterTap -= STEP_MS;
    rechargeOrdnance(player);
    // Run down the handling move, and clear the label with it so the renderer
    // and the input gate only ever have to test one field.
    if (player.transitionTime > 0) {
      player.transitionTime -= STEP_MS;
      if (player.transitionTime <= 0) {
        player.transitionTime = 0;
        player.transition = '';
      }
    }
    if (player.backflipping > 0) {
      player.backflipping -= STEP_MS;
      // Rotate 360 degrees over the animation duration
      const progress = (BACKFLIP_TIME - player.backflipping) / BACKFLIP_TIME;
      player.rotation = progress * Math.PI * 2; // Full rotation (2π radians)
      if (player.backflipping <= 0) {
        player.rotation = 0; // Reset rotation when animation completes
      }
    }
    if (player.vaporizing > 0) {
      player.vaporizing -= STEP_MS;
      if (player.vaporizing <= 0) {
        // Vaporization complete, respawn player
        completeRespawn(game, player, fx);
      }
      return; // Skip input and physics while vaporizing
    }

    // Hand-to-hand timers. Each clears its own label on expiry so the pose
    // picker and the input gate only ever test one field, the same shape the
    // handling transitions above use.
    if (player.attackTime > 0) {
      player.attackTime -= STEP_MS;
      if (player.attackTime <= 0) {
        player.attackTime = 0;
        player.attack = '';
        player.attackHit = false;
      }
    }
    if (player.hurtTime > 0) player.hurtTime -= STEP_MS;
    if (player.downTime > 0) {
      player.downTime -= STEP_MS;
      if (player.downTime <= 0) {
        player.downTime = 0;
        // Brief mercy on the way up. Getting swept twice in a row is a loop
        // with no way out of it, and this is the frame or two that breaks it.
        player.hurtTime = HURT_TIME;
      }
    }

    // A committed beam runs its windup to completion and fires itself — the
    // shot cannot be cancelled once started, which is what makes the tell
    // meaningful to the player being aimed at.
    if (player.beamCharge > 0) {
      player.beamCharge -= STEP_MS;
      if (player.beamCharge <= 0) {
        player.beamCharge = 0;
        fireBeam(game, player, fx);
      }
    }

    // Skip input if stunned, on the floor, or reeling from a hit.
    if (player.stunned > 0) return;
    if (player.downTime > 0 || player.hurtTime > 0) {
      player.guarding = false;
      return;
    }

    // Handle input. Asked for here, and only here, so the source sees the
    // world exactly as this fighter is about to act in it.
    const input = readInput(playerId, player, game);
    if (input) handlePlayerInput(game, player, input, fx);
  });

  // Update physics
  updatePhysics(game, fx);

  // Update beams
  updateBeams(game);
  updateOrdnance(game, fx);

  // Check collisions
  checkCollisions(game, fx);

  // Check screen advancement
  checkScreenAdvancement(game, fx);

  return true;
};

/* --- Board handling and input ---------------------------------------- */

/**
 * Start a board-handling animation: drawing the board up into the firing
 * grip, stowing it back underfoot, or stepping onto it.
 *
 * The renderer reads these to pick the transitional pose AND to fly the board
 * between its two resting places. Because the board is a separate sprite, the
 * travel is real interpolation rather than a cut — which is what makes the
 * move read as the rider handling an object rather than the object teleporting
 * between two poses.
 *
 * @param {object} player
 * @param {'draw'|'stow'|'mount'} type
 */
const beginTransition = (player, type) => {
  player.transition = type;
  player.transitionTime =
    type === 'draw' ? DRAW_TIME : type === 'stow' ? STOW_TIME : MOUNT_TIME;
};

/**
 * Apply one fighter's input frame: movement, board handling, ordnance and
 * melee, according to whether they are riding, aiming or on foot.
 *
 * @param {object} game
 * @param {object} player
 * @param {object} input Boolean input frame (see noInput in sync.js).
 * @param {object} fx
 */
const handlePlayerInput = (game, player, input, fx) => {
  // Winding up a shot commits the player: no switching, no kicking, and
  // barely any movement. That immobility is the cost that makes the beam's
  // telegraph a real trade rather than a free look.
  const charging = player.beamCharge > 0;

  // Double-tap guard: sling the board onto your back, or take it off again.
  // Read on the RISING EDGE of a held button, so holding guard still just
  // guards — the two meanings cannot collide because one is an edge and the
  // other is a hold.
  const guardEdge = !!input.guard && !player.guardHeld;
  player.guardHeld = !!input.guard;
  if (guardEdge && !charging) {
    if (player.holsterTap > 0) {
      player.holsterTap = 0;
      toggleHolster(player, fx);
    } else {
      player.holsterTap = HOLSTER_TAP_WINDOW;
    }
  }

  // Ordnance, before the board/on-foot split, because it belongs to the
  // fighter rather than to the board: throwable while riding in either mode,
  // and still throwable with the board gone — which matters, because the EMP
  // takes a board away for thirty seconds and having nothing at all to do for
  // thirty seconds is a worse punishment than the one intended.
  //
  // Not during a beam windup: that commitment already blocks switching and
  // kicking, and letting it through here would make the charge free.
  if (!charging) {
    if (input.emp && player.empAmmo > 0) fireEmp(game, player, fx);
    else if (input.cluster && player.clusterAmmo > 0) throwOrdnance(game, player, 'cluster', fx);
  }

  if (player.onBoard) {
    // Board mode switching. Blocked while a handling move is already running,
    // so hammering the key cannot restart the animation every frame and leave
    // the board stuck partway between the boots and the hands.
    if (input.switchMode && !charging && !player.transition) {
      // The mode flips immediately — movement and gravity change at once —
      // while the animation plays over the top of it. Only the beam waits.
      player.boardMode = player.boardMode === 'feet' ? 'hands' : 'feet';
      beginTransition(player, player.boardMode === 'hands' ? 'draw' : 'stow');
      fx.sound('switchMode');
    }

    if (player.boardMode === 'feet') {
      // === FEET MODE: Full hoverboard mobility ===
      if (input.left) {
        player.vx = Math.max(player.vx - BOARD_ACCEL, -BOARD_SPEED);
        player.facingRight = false;
      }
      if (input.right) {
        player.vx = Math.min(player.vx + BOARD_ACCEL, BOARD_SPEED);
        player.facingRight = true;
      }
      if (input.jump) {
        // Hoverboard smooth upward movement
        player.vy = Math.max(player.vy - 0.6, -BOARD_CLIMB_SPEED);
      }
      if (input.down) {
        // Hoverboard smooth downward movement
        player.vy = Math.min(player.vy + 0.6, BOARD_CLIMB_SPEED);
      }
      if (input.action) {
        // Kick board
        kickBoard(player, fx);
      }
      // NO beam shooting in feet mode
    } else if (player.boardMode === 'hands') {
      // === HANDS MODE: Limited movement, can shoot ===
      // Movement is cut to a crawl during the windup so a charging player
      // can't also reposition to chase a dodge.
      const mobility = charging ? 0.2 : 1;
      if (input.left) {
        player.vx = Math.max(player.vx - 0.4 * mobility, -PLAYER_SPEED * 0.7 * mobility);
        if (!charging) player.facingRight = false;
      }
      if (input.right) {
        player.vx = Math.min(player.vx + 0.4 * mobility, PLAYER_SPEED * 0.7 * mobility);
        if (!charging) player.facingRight = true;
      }
      if (input.jump && !charging && player.grounded) {
        player.vy = JUMP_FORCE * 0.6; // Limited jump when holding board
      }
      // The draw has to finish before a shot can even start winding up. This
      // is the whole gameplay weight of the animation: switching to Hands in
      // someone's face now costs time instead of being a free instant threat.
      if (input.beam && player.beamRecharge <= 0 && !charging
          && player.transition !== 'draw') {
        // Begin the committed windup; fireBeam() runs when it elapses.
        beginBeamCharge(player, fx);
      }
      if (input.action && !charging) {
        // Throw/kick board from hands
        kickBoard(player, fx);
      }
    }
  } else {
    // === ON FOOT, BOARD GONE: movement and hand-to-hand ===
    const grounded = !!player.grounded;
    // Crouching is a held state rather than a move, because it is the
    // modifier the low attacks are derived from — it has to still be true on
    // the frame the attack key goes down.
    // The guard BUTTON, not the stick. Holding a direction to mean something
    // other than a direction fights the hand that is steering, and nothing on
    // screen ever said the stick had a second job.
    player.guarding = !!input.guard && grounded && !player.attack;

    // A swing owns the body until it finishes. Movement during one is cut
    // rather than blocked outright, so a whiffed attack drifts to a stop
    // instead of nailing the fighter to the spot.
    const swinging = player.attack !== '';
    const mobility = swinging ? 0.15 : player.guarding ? 0.4 : 1;

    if (input.left) {
      player.vx = Math.max(player.vx - 0.6 * mobility, -PLAYER_SPEED * mobility);
      if (!swinging) player.facingRight = false;
    }
    if (input.right) {
      player.vx = Math.min(player.vx + 0.6 * mobility, PLAYER_SPEED * mobility);
      if (!swinging) player.facingRight = true;
    }
    if (input.jump && !swinging && !player.guarding) {
      // Character jump - only if on ground
      if (grounded) {
        player.vy = JUMP_FORCE;
      }
    }

    // Attacks. Nothing new can start while one is already running, which is
    // what makes a whiff punishable rather than something you mash out of.
    if (!swinging && (input.punch || input.kick)) {
      beginAttack(game, player, input, grounded, fx);
    }

    if (input.action) {
      // An EMP'd board is not a board. It answers to nothing until its timer
      // runs out, so the button that would normally fetch it does nothing —
      // deliberately silent rather than helpfully doing something else,
      // because the board is drawn dark and sparking and that is the answer.
      if (player.boardDown > 0) return;
      // Check if near board to pick up or recall board
      const nearBoard = isNearBoard(game, player);
      if (nearBoard && nearBoard.owner === player.board.owner) {
        // Don't allow re-mounting during the first 200ms of the backflip, so
        // a kick can't be instantly undone by the same button.
        if (player.backflipping > BACKFLIP_TIME - 200) {
          return; // Skip board pickup during early backflip
        }
        // Pick up own board (defaults to feet mode)
        mountBoard(player, fx);
      } else if (nearBoard && nearBoard.owner !== player.board.owner) {
        // Try to pick up opponent's board - get electrocuted
        player.stunned = SHOCK_STUN_TIME;
        fx.sound('shock');
        fx.particle(player.x, player.y, 'electric');
      } else if (!player.board.returning) {
        // Recall own board
        recallBoard(player, fx);
      }
    }
  }
};

/**
 * Start a melee swing, if the input frame is asking for one.
 *
 * The move is chosen here rather than in melee.js's pure chooser alone
 * because one of the modifiers is the *other player* — standing over someone
 * on the floor turns both attacks into finishers — and the opponent is only
 * visible from the game state.
 *
 * @param {object} game
 * @param {object} player
 * @param {object} input Boolean input frame.
 * @param {boolean} grounded Whether the fighter is standing on something.
 * @param {object} fx
 */
const beginAttack = (game, player, input, grounded, fx) => {
  const foe = opponentOf(game, player);
  // Reach past your own body toward them, not centre to centre, so the
  // finisher triggers from where you could actually stamp on someone.
  const overDowned = !!foe
    && foe.downTime > 0
    && Math.abs(foe.x - player.x) < FINISH_RANGE;

  const move = chooseMove(input, { airborne: !grounded, overDowned });
  if (!move) return;

  player.attack = move;
  player.attackTime = MOVES[move].time;
  player.attackHit = false;
  player.guarding = false; // the swing owns the pose now

  // A diving move throws away the arc that was carrying the fighter and
  // replaces it with its own. Set here rather than in the physics so the
  // launch happens on the frame the key was pressed — the commitment and the
  // input are the same instant, which is what makes the timing readable.
  const { dive } = MOVES[move];
  if (dive) {
    player.vx = (player.facingRight ? 1 : -1) * dive.speed;
    player.vy = dive.fall;
  }
  fx.sound(move === 'punch' || move === 'punchlow' || move === 'pound'
    ? 'punch' : 'swing');
};

/** The other player, for rules that need to see both. */
const opponentOf = (game, player) => {
  const { player1, player2 } = game.players;
  return player === player1 ? player2 : player1;
};

/**
 * Resolve one fighter's active strike against the other.
 *
 * Reads as an attack landing rather than as two boxes overlapping because of
 * the ordering: the strike only exists during its active frames, it only
 * lands once per swing, and being hit takes the victim's own swing away. That
 * last part is what makes trades resolve — without it whoever pressed first
 * still eats the answer, and spacing stops mattering.
 *
 * @param {object} game
 * @param {object} attacker
 * @param {object} victim
 * @param {object} fx
 */
const resolveStrike = (game, attacker, victim, fx) => {
  if (!attacker.attack || attacker.attackHit) return;
  if (!isStriking(attacker)) return;
  if (!isVulnerable(victim) || victim.vaporizing > 0) return;

  const move = MOVES[attacker.attack];
  // A downed fighter lies in the low band; a standing one fills their body
  // box. Testing the real box both ways is what makes a sweep pass under a
  // high guard and a stomp miss someone who has got back up.
  const hurtBox = victim.downTime > 0
    ? {
      left: victim.x - BODY_HALF_WIDTH * 1.6,
      right: victim.x + BODY_HALF_WIDTH * 1.6,
      top: victim.y - 16,
      bottom: victim.y + BODY_BOTTOM,
    }
    : getBodyBox(victim);

  if (!boxesOverlap(strikeBox(attacker, BODY_HALF_WIDTH), hurtBox)) return;

  attacker.attackHit = true;
  const away = Math.sign(victim.x - attacker.x) || (attacker.facingRight ? 1 : -1);

  // Blocked. The swing is spent either way — a guard eats the whole attack
  // rather than absorbing one frame of it and taking the rest — and the
  // defender loses ground and nothing else. Giving up position IS the cost of
  // turtling in a game about pushing someone across seven screens, which is
  // why the guard needs no stamina meter to stay honest.
  if (guardStops(victim, attacker, move)) {
    victim.vx += away * move.push * GUARD_PUSH_FRAC;
    fx.sound('block');
    fx.particle(
      victim.x - away * BODY_HALF_WIDTH, victim.y - 30, 'block'
    );
    return;
  }

  victim.hp -= move.damage;
  victim.vx += away * move.push;

  if (victim.hp <= 0) {
    fx.sound('vaporize');
    startVaporization(game, victim, attacker, fx);
    return;
  }

  // Interrupt whatever they were doing. A fighter already on the floor stays
  // there — re-flinching them would reset the knockdown and make the finisher
  // loop unescapable.
  if (victim.downTime <= 0) {
    victim.attack = '';
    victim.attackTime = 0;
    victim.attackHit = false;
    victim.guarding = false;
    if (move.knocksDown) {
      victim.downTime = KNOCKDOWN_TIME;
      victim.hurtTime = 0;
      fx.sound('dismount');
    } else {
      victim.hurtTime = HURT_TIME;
    }
  }

  fx.sound('hit');
  fx.particle(
    victim.x, victim.y + (move.band[0] + move.band[1]) / 2, 'hit'
  );
};

/**
 * Put the board on your back, or take it off again.
 *
 * The distinction that matters is against being DISMOUNTED: a dismounted
 * rider's board is loose in the world — it falls, it can be kicked back at
 * them, an opponent can grab it — whereas a holstered one is still theirs.
 * Everything that treats a board as loose has to know the difference, or a
 * fighter's own board starts hitting people from their spine.
 *
 * It costs the stow animation each way, which is the point: choosing to fight
 * with your fists should look like a decision, and 240ms of being neither one
 * thing nor the other is what that decision is worth.
 *
 * @param {object} player
 * @param {object} fx
 */
const toggleHolster = (player, fx) => {
  if (player.holstered) {
    // Back on. A dead board still refuses, exactly as it refuses everything.
    if (player.boardDown > 0) return;
    player.holstered = false;
    player.onBoard = true;
    player.boardMode = 'feet';
    beginTransition(player, 'mount');
    fx.sound('mount');
    return;
  }
  // Only from ON the board. With it already loose in the world there is
  // nothing on your back to sling, and pretending otherwise would be a free
  // recall from any distance.
  if (!player.onBoard) return;
  player.holstered = true;
  player.onBoard = false;
  player.boardMode = 'feet';
  player.beamCharge = 0;
  beginTransition(player, 'stow');
  fx.sound('switchMode');
};

/**
 * Send the board away. Two different moves share this path:
 *
 *  - A KICK, from Feet mode, punts the board out from under the rider. That
 *    is what the backflip is for — they are throwing away the thing they were
 *    standing on and tumbling off it.
 *  - A THROW, from Hands mode, releases a board already held out in front.
 *    There is nothing underfoot to push against, so there is no somersault
 *    and no hop.
 *
 * Running both through the backflip was a real bug rather than a cosmetic
 * one: a throw from Hands mode handed out a free ~70px of height and a second
 * of spinning, so releasing the board doubled as an escape move from a
 * standing aim.
 *
 * @param {object} player
 * @param {object} fx
 */
const kickBoard = (player, fx) => {
  // Read before anything is cleared: it decides which move this is.
  const kickedFromUnderfoot = player.boardMode === 'feet';

  player.onBoard = false;
  // Nothing is being held or ridden any more, so the mode must stop claiming
  // otherwise. Left on 'hands', a disarmed player still reads as one holding
  // the board — which is the state the AI's beam-threat check looks for.
  player.boardMode = 'feet';
  // The backflip takes over the whole body, so any half-finished draw or
  // mount is abandoned rather than left to fight it for the pose.
  player.transition = '';
  player.transitionTime = 0;
  player.beamCharge = 0; // can't be mid-windup without the board in hand
  fx.sound('kick');

  if (kickedFromUnderfoot) {
    // Start backflip animation with jump
    player.backflipping = BACKFLIP_TIME;
    player.rotation = 0; // Start rotation from 0

    // Backflip hop. Kept modest: the reduced backflip gravity (see
    // updatePhysics) means a large launch never stops rising within the 1s
    // flip and pins the player against the ceiling. -8 gives a ~90px arc.
    player.vy = -8;
  }

  // Launch board with slight offset so player doesn't immediately re-mount
  const launchDirection = player.facingRight ? 1 : -1;
  player.board.vx = launchDirection * 18; // Fast horizontal launch
  player.board.vy = -4; // Slightly more upward velocity
  player.board.x = player.x + (launchDirection * 10); // Start board slightly away from player
  // Leave from where the board actually was. A throw releases it from chest
  // height; starting it at the feet made it visibly drop through the rider
  // before flying off. MUZZLE_DY is that height: the held board's art is
  // placed from it (HELD_BOARD.y in sprites.js is defined as exactly this), and
  // reading the constant keeps the rules clear of the sprite module.
  player.board.y = kickedFromUnderfoot ? player.y : player.y + MUZZLE_DY;
  player.board.spin = 0; // begin the tumble from flat
  player.board.spent = false; // a fresh throw gets a fresh hit
};

/**
 * Find a board within grabbing range, preferring the player's own.
 *
 * The preference matters: when both boards are lying close together, a
 * plain first-match scan could hand back the opponent's board and shock a
 * player who was reaching for their own.
 *
 * @param {object} game
 * @param {object} player
 * @returns {object|null} the board in range, or null.
 */
const isNearBoard = (game, player) => {
  const own = player.board;
  const other =
    own.owner === 'player1' ? game.players.player2.board : game.players.player1.board;

  const inRange = (board) =>
    Math.hypot(player.x - board.x, player.y - board.y) < BOARD_MOUNT_RANGE;

  if (inRange(own)) return own;
  // A board on someone's back is not lying there to be grabbed. Without this
  // an opponent standing close enough would "reach" it and take the shock for
  // touching a board that is strapped to a moving fighter.
  const otherHolstered = game.players[other.owner].holstered;
  if (!otherHolstered && inRange(other)) return other;
  return null;
};

/**
 * Step onto your own board, taking on some of its momentum.
 *
 * @param {object} player
 * @param {object} fx
 */
const mountBoard = (player, fx) => {
  player.onBoard = true;
  fx.sound('mount');
  player.boardMode = 'feet'; // Default to feet mode when mounting
  beginTransition(player, 'mount');
  // Player inherits board's momentum for smooth transition
  player.vx = player.board.vx * 0.7; // Slightly reduce the momentum
  player.vy = player.board.vy * 0.7;
  player.x = player.board.x;
  player.y = player.board.y;
  
  // Reset board physics
  player.board.vx = player.vx;
  player.board.vy = player.vy;
  player.board.spin = 0;
  player.board.spent = false;
  player.board.returning = false;
};

/**
 * Call a loose board home; updateBoardPhysics flies it back.
 *
 * @param {object} player
 * @param {object} fx
 */
const recallBoard = (player, fx) => {
  // Start board return sequence
  player.board.returning = true;
  fx.sound('recall');
  fx.particle(player.board.x, player.board.y, 'recall');
};

/**
 * Locate the muzzle the beam fires from — the tip of the board held out as a
 * cannon. Shared by the windup glow and the shot itself so the charge and
 * the beam always originate from the same point.
 *
 * @param {object} player
 * @returns {{x: number, y: number, direction: number}}
 */
export const getMuzzle = (player) => {
  const direction = player.facingRight ? 1 : -1;

  if (player.boardMode === 'hands') {
    // The held board sprite is positioned to put its nose here, so the beam
    // leaves the barrel it looks like it leaves. See HELD_BOARD in sprites.js.
    return {
      x: player.x + direction * MUZZLE_DX,
      y: player.y + MUZZLE_DY,
      direction,
    };
  }
  return { x: player.x, y: player.y, direction };
};

/**
 * Begin the committed beam windup. The shot is locked in from here — it goes
 * off when beamCharge elapses (see stepGame) whether or not the key is
 * still held — so the opponent has a fixed, readable window to break the
 * firing line. Only being dismounted cancels it.
 *
 * @param {object} player
 * @param {object} fx
 */
const beginBeamCharge = (player, fx) => {
  player.beamCharge = BEAM_CHARGE_TIME;
  fx.sound('beamCharge');
};

/**
 * Fire the charged beam from the muzzle and start the recharge.
 *
 * @param {object} game
 * @param {object} player
 * @param {object} fx
 */
const fireBeam = (game, player, fx) => {
  const { x: muzzleX, y: beamY, direction } = getMuzzle(player);

  // Cover stops the shot. Resolving the endpoint once at fire time (rather
  // than per frame) keeps the drawn beam and its kill range identical, so
  // anything sheltered behind a slab is genuinely safe.
  //
  // Cast from the body, not the muzzle: the aimed board reaches far enough
  // forward that a rider pressed against a slab has the barrel tip poking out
  // the far side, and casting from there would let the shot start beyond the
  // very cover that should have blocked it.
  const endX = raycastX(game.currentScreen, player.x, beamY, direction);
  const beamX = direction > 0
    ? Math.min(muzzleX, endX)
    : Math.max(muzzleX, endX);

  game.beams.push({
    x: beamX,
    y: beamY,
    endX,
    direction,
    owner: player.board.owner,
    life: BEAM_LIFE,
  });

  player.beamRecharge = BEAM_RECHARGE_TIME;
  fx.sound('beam');
  fx.particle(beamX, beamY, 'beam');
};

/* --- Physics ---------------------------------------------------------- */

/**
 * Move both fighters and their boards one step: gravity, friction, the room's
 * walls, floor and ceiling, and the obstacles in it.
 *
 * @param {object} game
 * @param {object} fx Needed because a recalled board arriving home mounts.
 */
const updatePhysics = (game, fx) => {
  Object.keys(game.players).forEach(playerId => {
    const player = game.players[playerId];
    
    // A dive holds its line. Gravity would bend the descent into a curve and
    // friction would bleed the forward speed away over the flight, and both
    // would turn a committed straight attack back into an ordinary jump with
    // a pose on it. The trajectory is the move.
    const diving = isDiving(player);

    // Apply gravity - reduced when on hoverboard for hovering effect
    if (diving) {
      player.vy = MOVES[player.attack].dive.fall;
    } else if (player.onBoard) {
      player.vy += GRAVITY * 0.3; // Reduced gravity for hoverboard
    } else if (player.backflipping > 0) {
      // During backflip: slightly reduced gravity for a floaty flip that
      // still arcs back down well within the 1s animation (0.7, not 0.4 —
      // too-low gravity let the hop climb into the ceiling).
      player.vy += GRAVITY * 0.7;
    } else {
      player.vy += GRAVITY; // Full gravity when dismounted normally
    }

    // Apply friction
    if (!diving) player.vx *= FRICTION;
    if (player.onBoard) {
      player.vy *= 0.95; // Air resistance for hovering
    }
    
    // Update position
    player.x += player.vx;
    player.y += player.vy;
    
    // Screen boundaries - allow reaching edges for advancement
    if (player.x < 5) {
      player.x = 5; // Allow very close to edge
      player.vx = 0;
    }
    if (player.x > screenWidth(game.currentScreen) - 5) {
      player.x = screenWidth(game.currentScreen) - 5; // Allow very close to edge
      player.vx = 0;
    }
    // Ceiling: keep the whole character on screen. The tallest sprite (hands
    // pose) reaches ~56px above player.y, so the clamp stops the head
    // clipping off the top edge / behind the HUD.
    if (player.y < CEILING_Y) {
      player.y = CEILING_Y;
      player.vy = Math.max(player.vy, 0); // no further upward movement
    }

    // Standing on something is a fact about position, not about speed.
    // Everything used to infer it from `Math.abs(vy) < 0.1`, which is also
    // true for one frame at the apex of every jump — so a fighter at the top
    // of their arc counted as grounded, and got the standing kick instead of
    // the flying one (and could jump again from mid-air). Recorded here, at
    // the two places the simulation actually resolves a landing.
    player.grounded = false;

    // Ground collision
    if (player.y >= GROUND_Y) {
      player.y = GROUND_Y;
      player.grounded = true;
      if (player.onBoard) {
        player.vy = Math.max(player.vy * -0.2, -2); // Gentle bounce for hoverboard
      } else {
        player.vy = 0; // Hard stop for character
      }
    }

    resolveObstacleCollision(game, player);

    // Touching down ends a dive, hit or miss, and costs the fighter a beat
    // before they can act again. Without it a whiffed dive would leave them
    // free the instant they land, and a committed move that is safe on whiff
    // is not a commitment at all.
    if (diving && player.grounded) {
      player.attackTime = MOVES[player.attack].dive.recovery;
      player.attackHit = true; // the strike is over; it cannot connect twice
      player.vx *= 0.3; // skid, rather than sliding on at dive speed
    }

    // Update board physics
    if (!player.onBoard) {
      updateBoardPhysics(game, player.board, fx);
    } else {
      // Board follows player when mounted
      player.board.x = player.x;
      player.board.y = player.y;
      player.board.vx = player.vx;
      player.board.vy = player.vy;
    }
  });
};

/**
 * Push a fighter out of any obstacle they have entered, along whichever axis
 * they penetrated least.
 *
 * Resolving on the shallower axis is what makes a slab behave like a ledge
 * rather than a magnet: someone falling onto the top gets stood up, while
 * someone drifting into the side gets stopped horizontally, from the same
 * test and without tracking which face was approached.
 *
 * @param {object} game
 * @param {object} player
 */
const resolveObstacleCollision = (game, player) => {
  const feet = player.y;
  // A slim column around the fighter, so brushing past a ledge doesn't snag.
  const halfWidth = 10;
  const headroom = 44;

  getObstacles(game.currentScreen).forEach((o) => {
    const left = player.x - halfWidth;
    const right = player.x + halfWidth;
    const top = feet - headroom;
    if (right <= o.left || left >= o.right || feet <= o.top || top >= o.bottom) {
      return; // no overlap
    }

    const fromTop = feet - o.top; // depth entering through the upper face
    const fromBottom = o.bottom - top;
    const fromLeft = right - o.left;
    const fromRight = o.right - left;
    const smallest = Math.min(fromTop, fromBottom, fromLeft, fromRight);

    if (smallest === fromTop) {
      // Landed on it.
      player.y = o.top;
      player.grounded = true;
      if (player.onBoard) {
        player.vy = Math.max(player.vy * -0.2, -2); // hoverboard cushions
      } else {
        player.vy = Math.min(player.vy, 0);
      }
    } else if (smallest === fromBottom) {
      // Clipped the underside.
      player.y = o.bottom + headroom;
      player.vy = Math.max(player.vy, 0);
    } else if (smallest === fromLeft) {
      player.x = o.left - halfWidth;
      player.vx = Math.min(player.vx, 0);
    } else {
      player.x = o.right + halfWidth;
      player.vx = Math.max(player.vx, 0);
    }
  });
};

/**
 * Bounce a loose board off any obstacle it runs into.
 *
 * @param {object} game
 * @param {object} board
 */
const bounceBoardOffObstacles = (game, board) => {
  getObstacles(game.currentScreen).forEach((o) => {
    const box = getBoardBox(board);
    if (
      box.right <= o.left || box.left >= o.right ||
      box.bottom <= o.top || box.top >= o.bottom
    ) {
      return;
    }

    const fromTop = box.bottom - o.top;
    const fromBottom = o.bottom - box.top;
    const fromLeft = box.right - o.left;
    const fromRight = o.right - box.left;
    const smallest = Math.min(fromTop, fromBottom, fromLeft, fromRight);

    if (smallest === fromTop) {
      board.y = o.top - BOARD_HALF_HEIGHT;
      board.vy = -Math.abs(board.vy) * 0.7;
    } else if (smallest === fromBottom) {
      board.y = o.bottom + BOARD_HALF_HEIGHT;
      board.vy = Math.abs(board.vy) * 0.7;
    } else if (smallest === fromLeft) {
      board.x = o.left - BOARD_HALF_WIDTH;
      board.vx = -Math.abs(board.vx) * 0.8;
    } else {
      board.x = o.right + BOARD_HALF_WIDTH;
      board.vx = Math.abs(board.vx) * 0.8;
    }
  });
};

/**
 * Move a board that is not underfoot: slung on its owner's back, flying home
 * on a recall, or loose in the room and bouncing.
 *
 * @param {object} game
 * @param {object} board
 * @param {object} fx
 */
const updateBoardPhysics = (game, board, fx) => {
  // Slung across the back: pinned to its owner rather than simulated. Pinning
  // it here is also what draws it there — the loose-board renderer follows the
  // board's own position, so it needs no idea this state exists.
  const holder = game.players[board.owner];
  if (holder && holder.holstered) {
    board.x = holder.x - (holder.facingRight ? 11 : -11);
    board.y = holder.y - 28;
    board.vx = 0;
    board.vy = 0;
    board.spin = holder.facingRight ? 1.15 : -1.15;
    board.returning = false;
    return;
  }

  // Return to player logic - if recall was triggered
  if (board.returning) {
    const owner = game.players[board.owner];
    
    const dx = owner.x - board.x;
    const dy = owner.y - board.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    
    if (distance < 30) {
      // Board reached player. A board killed mid-recall coasts home and then
      // just lies there — the recall is the last thing it does.
      if (!owner.onBoard && owner.boardDown <= 0) {
        mountBoard(owner, fx);
      }
      return;
    } else if (distance > 0) {
      // Move board toward player - fast return speed
      const speed = 12;
      board.vx = (dx / distance) * speed;
      board.vy = (dy / distance) * speed;
      
      // Update position
      board.x += board.vx;
      board.y += board.vy;
    }
    return; // Skip normal physics when returning
  }
  
  // Normal board physics when not returning
  // Apply lighter gravity to board for more floaty behavior
  board.vy += GRAVITY * 0.6; // Reduced gravity for lighter feel

  // Update position
  board.x += board.vx;
  board.y += board.vy;

  // Tumble in the direction of travel, proportional to speed. Cosmetic only;
  // a thrown board that slid across the screen flat read as a static prop.
  board.spin = (board.spin || 0) + board.vx * 0.02;

  // Ground collision - bigger bounce, fewer bounces
  if (board.y > GROUND_Y) {
    board.y = GROUND_Y;
    
    // Big bounce with significant damping to prevent multiple bounces
    if (Math.abs(board.vy) > 1) {
      board.vy = -board.vy * 0.85; // Higher bounce coefficient
      board.vx *= 0.9; // Less horizontal speed reduction
    } else {
      // Stop micro-bouncing
      board.vy = 0;
      board.vx *= 0.8;
    }
  }
  
  // Wall collision - lighter bounces
  const roomW = screenWidth(game.currentScreen);
  if (board.x < 0 || board.x > roomW) {
    board.vx = -board.vx * 0.85; // Higher bounce coefficient for walls too
    board.x = board.x < 0 ? 0 : roomW;
  }

  bounceBoardOffObstacles(game, board);

  // Apply friction to slow down boards over time
  board.vx *= 0.995;
  board.vy *= 0.998;
};

/* --- Ordnance ---------------------------------------------------------
   Two thrown devices, neither of which kills. See the note in constants.js
   for why they exist; this is the machinery.

   One array holds both the canisters in flight and the submunitions a
   cluster shell splits into, because they differ only in what happens when
   the fuse runs out — and running one list means one set of rules for
   gravity, for what counts as a surface, and for what happens at a wall.
   -------------------------------------------------------------------- */

/** A fighter's body centre, which is what a blast measures its reach to. */
export const bodyCentre = (p) => ({ x: p.x, y: p.y + (BODY_TOP + BODY_BOTTOM) / 2 });

/**
 * Spend a charge, and start the refill clock IF that was the last one.
 *
 * The clock deliberately does not run while there is still something in the
 * magazine: three shells are three shells, and the minute only begins once
 * they are gone. From empty it hands them back one a minute, so a full reload
 * is three minutes and a fighter who has just emptied the magazine is
 * genuinely out rather than briefly inconvenienced.
 */
const spendCharge = (player, kind) => {
  const isEmp = kind === 'emp';
  const ammo = isEmp ? 'empAmmo' : 'clusterAmmo';
  const timer = isEmp ? 'empTimer' : 'clusterTimer';
  player[ammo] -= 1;
  if (player[ammo] <= 0 && player[timer] <= 0) {
    player[timer] = isEmp ? EMP_RECHARGE : CLUSTER_RECHARGE;
  }
};

/** Run one fighter's reload clocks. */
const rechargeOrdnance = (player) => {
  if (player.clusterTimer > 0) {
    player.clusterTimer -= STEP_MS;
    if (player.clusterTimer <= 0) {
      player.clusterAmmo += 1;
      player.clusterTimer = player.clusterAmmo < CLUSTER_MAX ? CLUSTER_RECHARGE : 0;
    }
  }
  if (player.empTimer > 0) {
    player.empTimer -= STEP_MS;
    if (player.empTimer <= 0) {
      player.empAmmo += 1;
      player.empTimer = player.empAmmo < EMP_MAX ? EMP_RECHARGE : 0;
    }
  }
};

/**
 * Lob a device.
 *
 * Thrown in FEET MODE with the beam button, which is the one control in the
 * game that genuinely did nothing: the beam needs Hands mode, so on a moving
 * board that button was dead — the touch pad even said so. Ordnance fills a
 * hole rather than displacing something, and it draws a clean line down the
 * middle of the two board modes: feet is speed and ordnance, hands is the
 * cannon, and you cannot have both at once.
 *
 * Holding DOWN picks the pulse over a shell — the same modifier the melee game
 * already uses for its low attacks, so the controls panel is teaching the
 * idiom already.
 *
 * The rider's own motion is folded into the throw at a fraction, so lobbing one
 * while running forward reaches further than from a standstill, without
 * letting a sprint flatten the arc.
 *
 * @param {object} game
 * @param {object} player
 * @param {'cluster'|'emp'} kind
 * @param {object} fx
 */
const throwOrdnance = (game, player, kind, fx) => {
  spendCharge(player, kind);

  const dir = player.facingRight ? 1 : -1;
  game.ordnance.push({
    kind,
    sub: false,
    x: player.x + dir * 18,
    y: player.y - 30,
    vx: dir * THROW_SPEED + player.vx * 0.4,
    vy: THROW_LIFT + player.vy * 0.3,
    spin: 0,
    fuse: CLUSTER_FUSE,
    owner: player.board.owner,
  });
  fx.sound('lob');
};

/**
 * A device reaching the end of its life.
 *
 * A cluster SHELL does not explode — it splits, scattering submunitions along
 * its travel which go off shortly after. That second stage is the entire
 * point of the weapon: it turns a point into a width of deck, and a width of
 * deck is what a fighter parked behind a slab cannot simply stand still
 * behind. Everything else detonates where it lies.
 *
 * @param {object} game
 * @param {object} d The device whose fuse or flight has ended.
 * @param {object[]} spawned Collects the submunitions a shell splits into.
 * @param {object} fx
 */
const burstOrdnance = (game, d, spawned, fx) => {
  if (d.kind === 'cluster' && !d.sub) {
    for (let i = 0; i < CLUSTER_SUBMUNITIONS; i++) {
      // Fanned around the shell's own heading, so the spread follows the
      // throw rather than always opening the same way. The lift and the fuse
      // are rolled from the game's own stream, not Math.random, so a replay
      // scatters them exactly where the match did.
      const across = (i / (CLUSTER_SUBMUNITIONS - 1) - 0.5) * 2;
      spawned.push({
        kind: 'cluster',
        sub: true,
        x: d.x,
        y: d.y - 4,
        vx: d.vx * 0.35 + across * SUBMUNITION_SPREAD,
        vy: -3.2 - nextRandom(game) * 2.2,
        spin: 0,
        fuse: SUBMUNITION_FUSE + nextRandom(game) * 160,
        owner: d.owner,
      });
    }
    fx.particle(d.x, d.y, 'hit');
    fx.sound('lob');
    return;
  }
  detonate(game, d.x, d.y, d.kind, d.owner, fx);
};

/**
 * A blast going off.
 *
 * Catches EVERYONE inside its radius, including whoever threw it. That is the
 * ordinary rule for a grenade and it is what makes the throw a decision about
 * spacing rather than a free button — the arc lands about 380px out,
 * clear of either radius, but a device lobbed at a wall a few paces away comes
 * back to the thrower's own feet.
 *
 * Spawn protection covers ordnance exactly as it covers beams and boards, so
 * a blast cannot camp a respawn.
 *
 * @param {object} game
 * @param {number} x
 * @param {number} y
 * @param {string} kind
 * @param {string} owner
 * @param {object} fx
 */
const detonate = (game, x, y, kind, owner, fx) => {
  game.blasts.push({ x, y, kind, life: BLAST_LIFE, max: CLUSTER_BLAST_RADIUS });
  fx.sound('clusterBurst');
  fx.particle(x, y, 'hit');
  fx.particle(x, y, 'hit');

  Object.values(game.players).forEach((p) => {
    if (!isVulnerable(p)) return;
    const c = bodyCentre(p);
    if (Math.hypot(c.x - x, c.y - y) > CLUSTER_BLAST_RADIUS) return;
    concuss(p, x, fx);
  });
  void owner; // symmetrical by design: the thrower is not exempt
  void kind;
};

/**
 * Fire the EMP.
 *
 * Not thrown. It leaves the fighter as a ring and runs to the edges of the
 * stage, so it cannot be aimed, cannot be dodged and cannot be wasted — see
 * EMP_SWEEP_TIME for why a once-every-three-minutes weapon should not be
 * something you can miss with.
 *
 * The radius is measured to whichever corner of this room is furthest away,
 * so "the whole stage" means the whole stage on an 800 landing screen and on
 * an 1800 goal screen alike.
 *
 * @param {object} game
 * @param {object} player
 * @param {object} fx
 */
const fireEmp = (game, player, fx) => {
  spendCharge(player, 'emp');

  const roomW = screenWidth(game.currentScreen);
  const from = bodyCentre(player);
  const reach = Math.hypot(
    Math.max(from.x, roomW - from.x),
    Math.max(from.y, CANVAS_HEIGHT - from.y)
  );

  game.blasts.push({
    x: from.x,
    y: from.y,
    kind: 'emp',
    life: EMP_SWEEP_TIME,
    max: reach,
    owner: player.board.owner,
    // Who the wavefront has already passed. Boards die as the edge reaches
    // them rather than all at once, which is what makes the ring mean
    // something instead of being decoration played after the fact.
    struck: [player.board.owner],
  });
  fx.sound('empBurst');
  for (let i = 0; i < 4; i++) fx.particle(from.x, from.y, 'electric');
};

/**
 * Advance the EMP wavefronts and strike what they reach.
 *
 * Cluster rings are pure decoration and are not considered here; their damage
 * was dealt at the moment they were drawn.
 *
 * @param {object} game
 * @param {object} fx
 */
const sweepBlasts = (game, fx) => {
  game.blasts.forEach((b) => {
    if (b.kind !== 'emp') return;
    const radius = b.max * (1 - b.life / EMP_SWEEP_TIME);
    Object.entries(game.players).forEach(([slot, p]) => {
      if (b.struck.includes(slot) || !isVulnerable(p)) return;
      const c = bodyCentre(p);
      if (Math.hypot(c.x - b.x, c.y - b.y) > radius) return;
      b.struck.push(slot);
      killBoard(p, fx);
    });
  });
};

/**
 * Cluster hit: takes a fighter's feet out from under them.
 *
 * Stun plus a dismount — an OPENING, which somebody still has to convert with
 * a beam, a board or their fists. The knock is away from the blast so a shell
 * landing between two fighters separates them rather than stacking them.
 *
 * @param {object} player
 * @param {number} blastX
 * @param {object} fx
 */
const concuss = (player, blastX, fx) => {
  player.stunned = Math.max(player.stunned, CLUSTER_STUN_TIME);
  player.beamCharge = 0;
  if (player.onBoard) {
    player.onBoard = false;
    player.boardMode = 'feet';
    player.transition = '';
    player.transitionTime = 0;
    fx.sound('dismount');
  }
  const away = Math.sign(player.x - blastX) || 1;
  player.vx = away * 4.5;
  player.vy = -6;
  fx.particle(player.x, player.y, 'hit');
};

/**
 * EMP hit: the board stops being a board.
 *
 * Not a stun and not damage — the rider is perfectly able to fight, just not
 * to ride, which drops them into the hand-to-hand game for the duration. A
 * board caught mid-recall gives up on the way home.
 *
 * @param {object} player
 * @param {object} fx
 */
const killBoard = (player, fx) => {
  player.boardDown = EMP_BOARD_DOWN_TIME;
  player.beamCharge = 0;
  player.board.returning = false;
  if (player.onBoard) {
    player.onBoard = false;
    player.boardMode = 'feet';
    player.transition = '';
    player.transitionTime = 0;
    player.vy = -3;
    fx.sound('dismount');
  }
  fx.sound('boardDown');
  fx.particle(player.x, player.y, 'electric');
};

/**
 * Advance every device in flight.
 *
 * A device goes off on the first thing it touches — deck, obstacle, wall or
 * fighter — and on its fuse if it touches nothing at all, so nothing thrown is
 * ever simply lost off the end of a room.
 *
 * @param {object} game
 * @param {object} fx
 */
const updateOrdnance = (game, fx) => {
  const roomW = screenWidth(game.currentScreen);
  const obstacles = getObstacles(game.currentScreen);
  const alive = [];
  const spawned = [];

  game.ordnance.forEach((d) => {
    d.fuse -= STEP_MS;
    d.vy += ORDNANCE_GRAVITY;
    d.x += d.vx;
    d.y += d.vy;
    d.spin += d.vx * 0.04;

    let done = d.fuse <= 0;
    if (d.y >= GROUND_Y) {
      d.y = GROUND_Y;
      done = true;
    }
    if (d.x <= 0 || d.x >= roomW) {
      d.x = Math.max(0, Math.min(roomW, d.x));
      done = true;
    }
    if (!done) {
      done = obstacles.some((o) =>
        d.x > o.left && d.x < o.right && d.y > o.top && d.y < o.bottom);
    }
    if (!done) {
      done = Object.values(game.players).some((p) =>
        isVulnerable(p)
        && Math.abs(p.x - d.x) < BODY_HALF_WIDTH + 8
        && d.y > p.y + BODY_TOP && d.y < p.y + BODY_BOTTOM);
    }

    if (done) burstOrdnance(game, d, spawned, fx);
    else alive.push(d);
  });

  game.ordnance = alive.concat(spawned);
  game.blasts = game.blasts.filter((b) => {
    b.life -= STEP_MS;
    return b.life > 0;
  });
  sweepBlasts(game, fx);
};

/* --- Beams, collisions, deaths ---------------------------------------- */

/**
 * Age the beams in the air and drop the spent ones.
 *
 * @param {object} game
 */
const updateBeams = (game) => {
  game.beams = game.beams.filter(beam => {
    beam.life -= STEP_MS;
    return beam.life > 0;
  });
};

/**
 * The character's hittable box. player.y is the FEET, so the box extends
 * upward from there. Every weapon tests against this one definition — board
 * collision used to measure a 35px radius around the feet, which let boards
 * pass straight through a rider's chest and head.
 *
 * @param {object} player
 * @returns {{left: number, right: number, top: number, bottom: number}}
 */
export const getBodyBox = (player) => ({
  left: player.x - BODY_HALF_WIDTH,
  right: player.x + BODY_HALF_WIDTH,
  top: player.y + BODY_TOP,
  bottom: player.y + BODY_BOTTOM,
});

/** The loose board's hittable box, centred on the board. */
export const getBoardBox = (board) => ({
  left: board.x - BOARD_HALF_WIDTH,
  right: board.x + BOARD_HALF_WIDTH,
  top: board.y - BOARD_HALF_HEIGHT,
  bottom: board.y + BOARD_HALF_HEIGHT,
});

/** Standard AABB overlap test. */
export const boxesOverlap = (a, b) =>
  a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;

/**
 * Whether a player can be hit at all right now: not already dying, and not
 * inside their spawn-protection window.
 */
export const isVulnerable = (player) => player.vaporizing <= 0 && player.invuln <= 0;

/**
 * Resolve everything that can hit a fighter this step: loose boards, beams,
 * and each other's fists and feet.
 *
 * @param {object} game
 * @param {object} fx
 */
const checkCollisions = (game, fx) => {
  const players = Object.values(game.players);

  // --- Board vs player -------------------------------------------------
  players.forEach(player => {
    players.forEach(otherPlayer => {
      if (player === otherPlayer) return;
        if (otherPlayer.onBoard || otherPlayer.holstered) return; // not in flight
      if (!isVulnerable(player)) return;

      const board = otherPlayer.board;
      // Each throw lands at most one hit. Without this a single board both
      // dismounts and then kills, because it is still overlapping the target
      // on the following frame — which made the two-stage design (knock them
      // off, then finish them) collapse into an instant kill.
      if (board.spent) return;

      const speed = Math.hypot(board.vx, board.vy);
      if (speed < BOARD_LETHAL_SPEED && !board.returning) return;

      if (!boxesOverlap(getBodyBox(player), getBoardBox(board))) return;

      board.spent = true;

      if (player.onBoard) {
        // Knock the rider off their board. This also breaks a beam windup,
        // which is the main answer to someone charging a shot at you.
        player.onBoard = false;
        player.beamCharge = 0;
        player.vx = board.vx * 0.5;
        player.vy = -5;
        fx.sound('dismount');
        fx.particle(player.x, player.y, 'hit');
      } else {
        // Already dismounted — a board hit is lethal.
        startVaporization(game, player, otherPlayer, fx);
      }
    });
  });

  // --- Beam vs player --------------------------------------------------
  game.beams.forEach(beam => {
    players.forEach(player => {
      if (beam.owner === player.board.owner) return;
      if (!isVulnerable(player)) return;

      // The beam spans from its muzzle to wherever cover stopped it.
      const box = getBodyBox(player);
      const crossesBody =
        beam.direction > 0
          ? box.right > beam.x && box.left < beam.endX
          : box.left < beam.x && box.right > beam.endX;
      if (!crossesBody) return;

      if (beam.y > box.top && beam.y < box.bottom) {
        const beamOwner = players.find(p => p.board.owner === beam.owner);
        startVaporization(game, player, beamOwner, fx);
      }
    });
  });

  // --- Hand to hand ----------------------------------------------------
  // Both strikes resolve before either can be cancelled, so a genuine
  // simultaneous trade lands both ways rather than going to whichever
  // fighter the player loop happened to visit first.
  resolveStrike(game, players[0], players[1], fx);
  resolveStrike(game, players[1], players[0], fx);
  separateFighters(players[0], players[1]);

  // NOTE: screen advancement is checked once per frame by stepGame (which
  // runs it right after collisions). A second call here used to make the win
  // branch fire twice on the winning frame — double score tally, double sound.
};

/**
 * Stop two fighters standing inside each other.
 *
 * This is all that is left of the tackle. Colliding used to BE the fight —
 * whoever was closing faster killed the other outright — which melee replaces
 * wholesale: an instant kill on contact means nobody would ever throw a
 * punch. What the old rule was also quietly doing, though, was keeping two
 * bodies from occupying the same pixels, and that still has to happen or
 * every exchange collapses into one overlapping blur with no readable
 * spacing.
 *
 * A downed fighter is not pushed: they are lying on the floor and their
 * attacker is meant to be able to stand over them.
 */
const separateFighters = (a, b) => {
  if (a.onBoard || b.onBoard) return; // riders pass; only fighters collide
  if (a.vaporizing > 0 || b.vaporizing > 0) return;
  if (a.downTime > 0 || b.downTime > 0) return;
  if (!boxesOverlap(getBodyBox(a), getBodyBox(b))) return;

  const toB = Math.sign(b.x - a.x) || 1;
  const overlap = BODY_HALF_WIDTH * 2 - Math.abs(b.x - a.x);
  // Split the correction between them so neither is authoritative over the
  // other's position, and damp rather than reverse the velocities — a bounce
  // here would fight the knockback the strikes apply.
  a.x -= toB * overlap * 0.5;
  b.x += toB * overlap * 0.5;
  a.vx *= 0.6;
  b.vx *= 0.6;
};

/**
 * Kill a player: play the vaporization death, hand the advancement right to
 * the killer, and freeze the simulation briefly so the hit lands.
 *
 * Every lethal source routes through here — board hits used to respawn the
 * victim instantly with no death animation, so half the kills in a match had
 * no moment of impact at all.
 *
 * @param {object} game
 * @param {object} player The fighter dying.
 * @param {?object} killer Who gets the push for it, if anyone.
 * @param {object} fx
 */
const startVaporization = (game, player, killer, fx) => {
  // Track the kill and unlock advancement
  if (killer) {
    game.advancementUnlocked = true;
    game.advancingPlayer = killer.board.owner;
    // A kill hands over the push, and the push is supposed to be a journey to
    // the door. Whoever just earned it may be standing in that door already —
    // defending it is exactly where a fight ends up — and the check below runs
    // every tick, so without this they are teleported through the instant the
    // kill lands, having chosen nothing. Disarmed here; re-armed once they are
    // clear of the edge.
    game.advanceArmed = false;
  }

  // Start vaporization animation
  player.vaporizing = VAPORIZE_TIME;
  player.vx = 0; // Stop movement
  player.vy = 0;
  player.beamCharge = 0; // a shot in the pipe dies with its owner
  game.hitStop = HIT_STOP_TIME;
  fx.sound('vaporize');
  // The death itself is drawn, not simulated: drawPlayerSprite reads the
  // vaporizing timer and collapses the rider's own art (see dissolve.js).
  // There is nothing to spawn here.
};

/**
 * Bring a fighter back once their vaporization has played out: at their own
 * end of the current room, on a working board, whole, and briefly protected.
 *
 * @param {object} game
 * @param {object} player
 * @param {object} fx
 */
const completeRespawn = (game, player, fx) => {
  const slot = player.board.owner;

  // Reset player to their spawn on the current screen — a fraction of that
  // screen's width, so a fighter always reappears at their own end of
  // whatever room the fight has reached rather than at a fixed coordinate
  // that would be barely a twelfth of the way into a goal screen.
  const spawn = spawnAt(slot, game.currentScreen);
  player.x = spawn.x;
  player.y = spawn.y;
  player.vx = 0;
  player.vy = 0;
  player.onBoard = true;
  player.boardMode = 'feet'; // Reset to feet mode on respawn
  player.facingRight = slot === 'player1';
  player.stunned = 0;
  player.vaporizing = 0;
  // A respawn hands back a working board, so an EMP does not outlive the
  // fighter it caught — stacking a dead board on top of a death would make one
  // pulse worth more than a kill. Whatever they were carrying is lost with
  // them; the canister does not come back through the respawn.
  player.boardDown = 0;
  player.holstered = false;
  player.backflipping = 0; // Reset backflip state
  player.rotation = 0; // Reset rotation
  player.beamCharge = 0;
  // A fresh body. Melee damage does not carry across a death — but note it
  // deliberately DOES carry across a retreat, so backing off to remount your
  // board is an escape, not a heal.
  player.hp = MELEE_MAX_HP;
  player.attack = '';
  player.attackTime = 0;
  player.attackHit = false;
  player.downTime = 0;
  player.hurtTime = 0;
  player.guarding = false;
  // Spawn protection: without it an opponent parked on the spawn point could
  // chain-kill on arrival, since respawns are at a fixed position.
  player.invuln = SPAWN_INVULN_TIME;

  // Reset board
  player.board.x = player.x;
  player.board.y = player.y;
  player.board.vx = 0;
  player.board.vy = 0;
  player.board.spin = 0;
  player.board.returning = false;

  fx.particle(player.x, player.y, 'respawn');
};

/* --- The push and the win --------------------------------------------- */

/**
 * A fighter has gone out through their goal portal: that game is theirs.
 *
 * In a one-off match that is the whole result. In a staked series it is one
 * game of three, and the series — with the money on it — is only decided when
 * somebody reaches the target. The all-time record counts SERIES rather than
 * the games inside one, so a best-of-three is a single entry either way.
 *
 * Settling is guarded by its own flag rather than by `winner`, because the
 * winner of the LAST game stays set while the end overlay is up: without the
 * guard, a second pass through here would pay the stake out twice.
 *
 * The bookkeeping itself — the tally, the wallet — is fx.recordWin and
 * fx.settleSeries: this decides WHEN, and whoever holds the books decides how.
 *
 * @param {object} game
 * @param {string} slot 'player1' | 'player2'.
 * @param {object} fx
 */
const concludeGame = (game, slot, fx) => {
  game.winner = slot;
  fx.particle(screenWidth(game.currentScreen) / 2, CANVAS_HEIGHT / 2, 'victory');
  fx.sound('win');

  const { series } = game;
  if (!series) {
    // Persist the win to the all-time tally and stash the updated record so
    // renderGame can show it on the win overlay.
    game.matchRecord = fx.recordWin(game.gameMode, slot, game.difficulty);
    return;
  }

  series.wins[slot] += 1;
  if (series.wins[slot] < series.target) return; // series continues

  game.matchRecord = fx.recordWin(game.gameMode, slot, game.difficulty);
  if (!series.settled) {
    series.settled = true;
    // The stake left the wallet when the series began, so a win credits the
    // whole payout back and a loss credits nothing.
    game.payout = fx.settleSeries(series.stake, series.difficulty, slot === 'player1');
  }
};

/**
 * Is a staked series running, and still undecided?
 *
 * @param {object} game
 * @returns {boolean}
 */
export const seriesLive = (game) =>
  !!game.series && !Object.values(game.series.wins).some(
    (w) => w >= game.series.target
  );

/**
 * Hand the push through the door once the fighter who earned it reaches it:
 * into the next room, or out through their goal portal to win the game.
 *
 * @param {object} game
 * @param {object} fx
 */
const checkScreenAdvancement = (game, fx) => {
  // Match already decided — never re-enter the win branch (its side effects,
  // score tally and victory sound, must fire exactly once).
  if (game.winner) return;

  // Only check if advancement is unlocked
  if (!game.advancementUnlocked || !game.advancingPlayer) return;
  
  const advancingPlayerObj = game.players[game.advancingPlayer];
  if (!advancingPlayerObj) return;
  
  // Check if advancing player reached screen edge with more lenient detection
  const reachedLeftEdge = advancingPlayerObj.x <= 10;
  const reachedRightEdge =
    advancingPlayerObj.x >= screenWidth(game.currentScreen) - 10;

  // Re-arm once they are clear of both doorways. Until then the push is
  // theirs but the door will not open — they have to walk out of it and come
  // back, which is the difference between crossing and being pushed.
  if (!game.advanceArmed) {
    if (!reachedLeftEdge && !reachedRightEdge) game.advanceArmed = true;
    return;
  }

  if (reachedLeftEdge || reachedRightEdge) {
    let newScreen = game.currentScreen;
    let shouldAdvance = false;

    // Player 1 (GREEN) advances RIGHT to their goal (screens 2→3→4→WIN)
    if (game.advancingPlayer === 'player1' && reachedRightEdge) {
      newScreen = game.currentScreen + 1;
      shouldAdvance = true;
    }
    // Player 2 (RED) advances LEFT to their goal (screens 2→1→0→WIN)
    else if (game.advancingPlayer === 'player2' && reachedLeftEdge) {
      newScreen = game.currentScreen - 1;
      shouldAdvance = true;
    }

    if (!shouldAdvance) {
      return; // Wrong direction
    }

    // The wall only opens at its portal, floating at mid-height. Arriving
    // outside the band presses the fighter against glass instead of
    // through it — they have to line up with it (a full jump peaks inside
    // the band, so grounded fighters get through too). The renderer shows
    // a shimmer at the contact point so a blocked push reads as "hit
    // glass", not "the game ignored me".
    if (!fitsThroughCellDoor(advancingPlayerObj.y)) {
      return;
    }

    // Going through your goal portal wins the game for whoever went through
    // it. Only checked when trying to push past the final screen on either
    // end — everything short of that is an ordinary screen advance.
    if (game.advancingPlayer === 'player1' && newScreen > SCREEN_COUNT - 1) {
      concludeGame(game, 'player1', fx); // GREEN, out past screen 6
      return;
    } else if (game.advancingPlayer === 'player2' && newScreen < 0) {
      concludeGame(game, 'player2', fx); // RED, out past screen 0
      return;
    }

    // Valid screen advancement (0 to 6)
    if (newScreen >= 0 && newScreen <= SCREEN_COUNT - 1) {
      advanceToScreen(game, newScreen, reachedRightEdge ? 1 : -1, fx);
    }
  }
};

/**
 * Push the fight into the next screen.
 *
 * The advancing player carries through the transition: they re-enter from
 * the edge they just crossed, keeping their height, momentum and board mode.
 * Previously both fighters were hard-reset to spawn, which zeroed the
 * attacker's momentum and threw away the pressure they had just earned — the
 * reward for breaking through was a full stop.
 *
 * The defender falls back to a clean defensive position ahead of the advance
 * with spawn protection, so arriving at speed isn't an automatic re-kill.
 *
 * @param {object} game
 * @param {number} newScreen Destination screen index (0..6).
 * @param {number} direction +1 if the push was rightward, -1 if leftward.
 * @param {object} fx
 */
const advanceToScreen = (game, newScreen, direction, fx) => {
  const fromScreen = game.currentScreen;
  game.currentScreen = newScreen;
  fx.sound('advance');
  // DO NOT lock advancement - advancing player continues until killed

  const advancerId = game.advancingPlayer;
  const defenderId = advancerId === 'player1' ? 'player2' : 'player1';
  const advancer = game.players[advancerId];
  const defender = game.players[defenderId];

  // Advancer walks in through the far edge, still moving. Measured against
  // the room they are arriving IN, not the one they left — the two are
  // different sizes now, and using the old room's width would drop them
  // hundreds of pixels inside a goal screen or outside a landing stage.
  const roomW = screenWidth(newScreen);
  advancer.x = direction > 0 ? 40 : roomW - 40;
  advancer.vx = direction * Math.max(Math.abs(advancer.vx), 2);
  advancer.facingRight = direction > 0;
  // Their board comes with them rather than being left on the old screen.
  advancer.board.x = advancer.x;
  advancer.board.y = advancer.y;
  advancer.board.vx = 0;
  advancer.board.vy = 0;
  advancer.board.spin = 0;
  advancer.board.returning = false;

  // Defender regroups ahead of the push, mounted and briefly protected.
  // The defender falls back to their own end of the new room. As a
  // fraction, so on a wide screen they regroup with real ground to give,
  // which is the point of the wide screens.
  defender.x = spawnAt(defenderId, newScreen).x;
  defender.y = spawnAt(defenderId, newScreen).y;
  defender.vx = 0;
  defender.vy = 0;
  defender.onBoard = true;
  defender.boardMode = 'feet';
  defender.facingRight = direction < 0;
  defender.beamCharge = 0;
  defender.backflipping = 0;
  defender.rotation = 0;
  defender.stunned = 0;
  defender.invuln = SPAWN_INVULN_TIME;
  defender.board.x = defender.x;
  defender.board.y = defender.y;
  defender.board.vx = 0;
  defender.board.vy = 0;
  defender.board.spin = 0;
  defender.board.returning = false;

  // Clear beams (the old room's sparks go in fx.screenChange, below)
  game.beams = [];
  // Devices in flight belong to the room that was left; a shell arcing over
  // the old deck has nothing to land on here. The canister goes with them and
  // a fresh one is placed on the clock, so arriving in a new room is never
  // arriving to somebody else's ordnance already in the air.
  game.ordnance = [];
  game.blasts = [];

  // Dolly into the new room, plus a short hold so the breakthrough reads as
  // an event rather than a hard cut. Play resumes well before the camera
  // finishes arriving. The dolly, and the sparks spilling off the gate the
  // advancer just came through, are the presentation's side of this.
  fx.screenChange(fromScreen, direction, advancerId);
  game.hitStop = SCREEN_TRANSITION_HOLD;
};
